import { useMemo, useState } from 'react';
import { Menu, Typography } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAddressCard,
  faAward,
  faBook,
  faBriefcase,
  faBuilding,
  faCheckDouble,
  faCheckSquare,
  faCity,
  faExclamationTriangle,
  faFile,
  faFileExcel,
  faFilePdf,
  faFlagUsa,
  faGlobeAmericas,
  faHome,
  faHouseUser,
  faIdBadge,
  faIdCard,
  faListAlt,
  faMoneyCheck,
  faNewspaper,
  faPenFancy,
  faPeopleArrows,
  faProjectDiagram,
  faScroll,
  faSitemap,
  faUniversity,
  faUsers,
  faUsersCog,
  faUserTag,
  faVoteYea,
} from '@fortawesome/free-solid-svg-icons';

import {
  getLocalStorage,
  getPermissions,
  IsRole,
} from '../../../utils/helpers/helpers.js';
import INTERNAL_LINKS from '../../../enums/InternalLinks';

const { Title } = Typography;

const MenuApp = ({ collapsed = false }) => {
  let location = useLocation();

  const [manager] = useState(IsRole(1));
  const [coordinator] = useState(IsRole(2));
  const [legal] = useState(IsRole(3));

  const [administrative] = useState(IsRole(4));
  const [assistant] = useState(IsRole(5));
  const [superUser] = useState(IsRole(6));
  const [contractor] = useState(IsRole(7));
  const [lead] = useState(IsRole(8));
  const [principal] = useState(IsRole(9));
  const [analyst] = useState(IsRole(11));
  const [validator] = useState(IsRole(12));

  const [person_id] = useState(getLocalStorage('tercero'));
  const [person_type_id] = useState(parseInt(getLocalStorage('person_type')));
  const [selectedKey] = useState([location.pathname]);

  const defaultOpenKeys = [
    'item-submenu-proveedores',
    'item-submenu-procesos',
    'item-submenu-configuracion',
    'item-submenu-parametros',
    'item-submenu-reportes',
    'item-submenu-Gestión de proyectos',
  ];

  const [perms] = useState(() => getPermissions());

  const [gpPerms] = useState(
    JSON.parse(window.localStorage.getItem('gp_permissions')),
  );

  const gpItems = [
    // TODO: Create here the object configs for menu items that should be displayed inside the 'Gestión de proyectos' submenu
    {
      perm: (p) => true,
      to: INTERNAL_LINKS.UNITS_STRUCTURE,
      label: 'Unidades - Estructura',
      icon: faBuilding,
    },
    {
      perm: (p) => true,
      to: INTERNAL_LINKS.UNITS_PEOPLE,
      label: 'Unidades',
      icon: faHouseUser,
    },
    {
      perm: (p) => true,
      to: INTERNAL_LINKS.ADMINISTRATION_FORMS,
      label: 'Formularios - Administración',
      icon: faFile,
    },
    {
      perm: (p) => true,
      to: INTERNAL_LINKS.UNITS_FORMS,
      label: 'Unidades - Formularios',
      icon: faNewspaper,
    },
    {
      perm: (p) => true,
      to: INTERNAL_LINKS.FORMS_FILL_OUT,
      label: 'Formularios - Diligenciamiento',
      icon: faPenFancy,
    },
  ];

  function excludeEmpty(i) {
    return i?.children ? i.children.length > 0 : true;
  }

  function filterMapChildren(c) {
    return c
      .filter((i) => (i?.perm ? i.perm(perms) : true))
      .map((v) => ({
        key: v.to || v.key,
        icon: v.icon ? (
          v?.to ? (
            <Link to={v.to}>
              <FontAwesomeIcon icon={v.icon} />
            </Link>
          ) : (
            <FontAwesomeIcon icon={v.icon} />
          )
        ) : null,
        label: v.to ? <Link to={v.to}>{v.label}</Link> : v.label,
        type: v?.type,
        title: v?.title,
        ...(v.children ? { children: filterMapChildren(v.children) } : {}),
      }))
      .filter(excludeEmpty);
  }

  const menuItems = useMemo(() => {
    const fullList = filterMapChildren([
      {
        to: INTERNAL_LINKS.HOME,
        icon: faHome,
        label: 'Inicio',
      },
      {
        perm: () => !superUser,
        key: 'ItemMenuGroup0',
        label: <Title level={4}>Personal</Title>,
        icon: faIdCard,
        type: 'group',
        children: [
          {
            to: `${
              person_type_id === 1
                ? INTERNAL_LINKS.NATURAL_PERSON
                : INTERNAL_LINKS.LEGAL_PERSON
            }/${person_id}`,
            icon: faIdCard,
            label: 'Perfil',
          },
        ],
      },
      {
        perm: () => !superUser,
        key: 'item-submenu-contratos',
        label: <Title level={5}>Contratos</Title>,
        children: [
          {
            to: INTERNAL_LINKS.RESUME_CONTRACTS,
            icon: faBook,
            label: 'Documentos contractuales',
          },
          {
            to: INTERNAL_LINKS.CONTRACTOR_INSURANCE,
            icon: faBook,
            label: 'Pólizas',
          },
          {
            to: INTERNAL_LINKS.CONTRACTOR_CT_CERTIFICATIONS,
            icon: faBook,
            label: 'Certificaciones',
          },
        ],
      },
      // {
      //   perm: () => contractor,
      //   key: 'item-submenu-Gestión de proyectos',
      //   label: <Title level={5}>Gestión de proyectos</Title>,
      //   children: gpItems,
      // },
      {
        perm: () => !contractor,
        key: 'ItemMenuGroup1',
        type: 'group',
        label: <Title level={4}>Gestión Administrativa</Title>,
        children: [
          {
            key: 'item-submenu-proveedores',
            label: <Title level={5}>Proveedores/Consultores</Title>,
            children: [
              {
                perm: () => perms['Hojas de vida']?.Leer,
                to: INTERNAL_LINKS.RESUME_SEARCH,
                label: 'Hojas de vida',
                icon: faUsers,
              },
              {
                perm: () => superUser,
                label: 'Edición',
                icon: faIdBadge,
                children: [
                  {
                    to: INTERNAL_LINKS.NATURAL_PERSONS,
                    icon: faIdCard,
                    label: 'Persona natural',
                  },
                  {
                    to: INTERNAL_LINKS.LEGAL_PERSONS,
                    icon: faAddressCard,
                    label: 'Persona jurídica',
                  },
                ],
              },
              {
                perm: () => legal || superUser,
                to: INTERNAL_LINKS.RESTRICTIVE_LIST_SEARCH,
                icon: faExclamationTriangle,
                label: 'Listas restrictivas',
              },
            ],
          },
          {
            key: 'item-submenu-procesos',
            label: <Title level={5}>Procesos</Title>,
            children: [
              {
                perm: () =>
                  manager ||
                  coordinator ||
                  administrative ||
                  superUser ||
                  assistant ||
                  principal ||
                  analyst ||
                  lead,
                to: INTERNAL_LINKS.ANNOUNCEMENT,
                label: 'Convocatorias',
                icon: faScroll,
              },
              {
                perm: () =>
                  manager ||
                  coordinator ||
                  administrative ||
                  superUser ||
                  assistant ||
                  principal ||
                  analyst ||
                  validator ||
                  lead,
                to: INTERNAL_LINKS.ANNOUNCEMENT_PRESELECTION,
                label: 'Preselección',
                icon: faCheckDouble,
              },
              {
                perm: () =>
                  manager ||
                  coordinator ||
                  administrative ||
                  superUser ||
                  assistant ||
                  legal ||
                  principal ||
                  analyst ||
                  validator ||
                  lead,
                to: INTERNAL_LINKS.ANNOUNCEMENT_SELECTION,
                label: 'Selección',
                icon: faVoteYea,
              },
              {
                perm: () =>
                  perms['Definición de Cargo y Equipo de Trabajo']?.Leer,
                to: INTERNAL_LINKS.PROJECT_TEAM,
                label: 'Equipo de trabajo',
                icon: faUsers,
              },
              {
                perm: () => perms['TDR']?.Leer,
                to: INTERNAL_LINKS.TDR_HOME,
                label: 'TDR',
                icon: faListAlt,
                title: 'Términos de referencia',
              },
              {
                perm: () => superUser || assistant,
                to: INTERNAL_LINKS.DOCUMENT_VALIDATION_PRECONTRACTUAL,
                label: 'Validación de documentos Precontractuales',
                icon: faCheckSquare,
                title: 'Validación de documentos Precontractuales',
              },
              {
                perm: () => superUser || legal,
                to: INTERNAL_LINKS.DOCUMENT_VALIDATION_CONTRACTUAL,
                label: 'Validación de documentos Jurídico',
                icon: faCheckSquare,
                title: 'Validación de documentos Jurídico',
              },
              {
                perm: () => superUser || assistant,
                to: INTERNAL_LINKS.DOCUMENT_VALIDATION_PARTIAL,
                label: 'Seguimiento Validación Documentación Parcial',
                icon: faCheckSquare,
                title: 'Seguimiento Validación Documentación Parcial',
              },
              {
                perm: () => perms['Búsqueda de documentos contractuales']?.Leer,
                to: INTERNAL_LINKS.RESUME_CONTRACT_SEARCH,
                label: 'Búsqueda de Documentos contractuales',
                icon: faBook,
                title: 'Búsqueda de Documentos contractuales',
              },
              {
                perm: () =>
                  perms['Aprobación de productos y/o entregables y gastos']
                    ?.Leer,
                to: INTERNAL_LINKS.PRODUCT_APPROVAL,
                label: 'Aprobación de productos y/o entregables y gastos',
                icon: faCheckSquare,
                title: 'Aprobación de productos y/o entregables y gastos',
              },
              {
                perm: () => principal,
                to: INTERNAL_LINKS.DOCUMENT_VALIDATION_DIRECTOR,
                label: 'Firma de TDR',
                icon: faCheckSquare,
                title: 'Firma de Términos de referencia',
              },
              {
                perm: () => perms['Rechazo de TDR']?.Leer,
                to: INTERNAL_LINKS.REJECT_TDR,
                label: 'Rechazos contratista',
                icon: faCheckSquare,
              },
              {
                perm: () => perms['Modificaciones Contractuales']?.Leer,
                to: INTERNAL_LINKS.CONTRACT_MODIFICATIONS,
                label: 'Modificaciones Contractuales',
                icon: faListAlt,
              },
              {
                perm: () => perms['Firma Modificaciones Contractuales']?.Leer,
                to: INTERNAL_LINKS.MODIFICATION_DIRECTOR,
                label: 'Firma Modificaciones Contractuales',
                icon: faCheckSquare,
                title: 'Firma Modificaciones Contractuales',
              },
              {
                perm: () => perms['Revisión Pólizas']?.Leer,
                to: INTERNAL_LINKS.INSURANCE,
                label: 'Revisión Pólizas',
                icon: faListAlt,
              },
              {
                perm: () => perms['Poscontractual']?.Leer,
                to: INTERNAL_LINKS.POSTCONTRACTUAL,
                label: 'Poscontractual',
                icon: faListAlt,
              },
              {
                perm: () => perms['Firma Poscontractual']?.Leer,
                to: INTERNAL_LINKS.POSTCONTRACTUAL_DIRECTOR,
                label: 'Firma Poscontractual',
                icon: faListAlt,
              },
              {
                perm: () => perms['Seguimiento Poscontractual']?.Leer,
                to: INTERNAL_LINKS.POSTCONTRACTUAL_FOLLOWUP,
                label: 'Seguimiento Poscontractual',
                icon: faListAlt,
              },
              {
                perm: () => perms['Certificaciones']?.Leer,
                to: INTERNAL_LINKS.CT_CERTIFICATIONS,
                label: 'Certificaciones',
                icon: faBook,
              },
            ],
          },
          {
            key: 'item-submenu-configuracion',
            label: <Title level={5}>Configuración</Title>,
            children: [
              {
                perm: () => superUser || legal || lead,
                to: INTERNAL_LINKS.ORGANIZATIONS,
                label: 'Organizaciones ejecutoras',
                icon: faSitemap,
              },
              {
                perm: () => superUser || legal || lead,
                to: INTERNAL_LINKS.CLIENTS,
                label: 'Clientes-Aliados',
                icon: faPeopleArrows,
              },
              {
                perm: () => superUser,
                to: INTERNAL_LINKS.COSTS,
                label: 'Centro de Costos',
                icon: faUniversity,
              },
              {
                perm: () => perms['Proyecto']?.Editar,
                to: INTERNAL_LINKS.PROJECTS,
                label: 'Proyectos',
                icon: faProjectDiagram,
              },
              {
                perm: () =>
                  coordinator ||
                  assistant ||
                  manager ||
                  legal ||
                  superUser ||
                  lead,
                to: INTERNAL_LINKS.EMPLOYEES,
                label: 'Empleados',
                icon: faSitemap,
              },
            ],
          },
          {
            key: 'item-submenu-parametros',
            label: <Title level={5}>Parámetros</Title>,
            children: [
              {
                perm: () => superUser,
                to: INTERNAL_LINKS.ROLES_MANAGEMENT,
                label: 'Administración de roles',
                icon: faUsersCog,
              },
              {
                perm: () => legal || superUser,
                to: INTERNAL_LINKS.TEMPLATES,
                label: 'Plantillas',
                icon: faFileExcel,
              },
              {
                perm: () => legal || superUser,
                to: INTERNAL_LINKS.POLITICS,
                label: 'Políticas',
                icon: faAward,
              },
              {
                perm: () => superUser,
                to: INTERNAL_LINKS.HELP_FORMAT,
                label: 'Ayudas Formato PDF',
                icon: faFilePdf,
              },
              {
                perm: () => superUser,
                to: INTERNAL_LINKS.AREAS,
                label: 'Áreas',
                icon: faBriefcase,
              },
              {
                perm: () => superUser,
                to: INTERNAL_LINKS.CURRENCY_TYPES,
                label: 'Tipos de monedas',
                icon: faMoneyCheck,
              },
              {
                perm: () => superUser || legal,
                to: INTERNAL_LINKS.ORGANIZATIONS_TYPES,
                label: 'Tipos de Organizaciones',
                icon: faSitemap,
              },
              {
                perm: () => superUser,
                to: INTERNAL_LINKS.COUNTRIES,
                label: 'Países',
                icon: faGlobeAmericas,
              },
              {
                perm: () => superUser,
                to: INTERNAL_LINKS.STATES,
                label: 'Departamentos',
                icon: faFlagUsa,
              },
              {
                perm: () => superUser,
                to: INTERNAL_LINKS.CITIES,
                label: 'Ciudades',
                icon: faCity,
              },
              {
                perm: () => superUser,
                to: INTERNAL_LINKS.IDENTITIES_TYPES,
                label: 'Tipo de Identificación',
                icon: faIdCard,
              },
            ],
          },
          {
            perm: () => superUser,
            key: 'item-submenu-reportes',
            label: <Title level={5}>Reportes</Title>,
            children: [
              {
                to: INTERNAL_LINKS.REPORTS_TDR,
                label: 'Reportes',
                icon: faIdCard,
              },
            ],
          },
          {
            perm: () => superUser,
            key: 'item-submenu-sicronizacion',
            label: <Title level={5}>Sincronización</Title>,
            children: [
              {
                to: INTERNAL_LINKS.SIGAAPP,
                label: 'Descarga programa',
                icon: faSitemap,
              },
            ],
          },
          // {
          //   key: 'item-submenu-Gestión de proyectos',
          //   label: <Title level={5}>Gestión de proyectos</Title>,
          //   children: [
          //     {
          //       perm: (p) => p['Proyectos - Gestión de perfiles']?.Leer,
          //       to: INTERNAL_LINKS.PROJECTS_PROFILE,
          //       label: 'Gestión de perfiles',
          //       icon: faUsers,
          //     },
          //     {
          //       perm: (p) => p['Personas - Proyecto']?.Leer,
          //       to: INTERNAL_LINKS.PROJECTS_PERSON,
          //       label: 'Personas - Proyecto',
          //       icon: faUserTag,
          //     },
          //     ...gpItems,
          //   ],
          // },
        ],
      },
    ]);
    return fullList;
  }, [
    perms,
    person_id,
    person_type_id,
    contractor,
    superUser,
    legal,
    administrative,
    analyst,
    assistant,
    coordinator,
    lead,
    manager,
    principal,
    validator,
  ]);

  const collapsedMenuItems = useMemo(() => {
    function flattenIL(pv, cv) {
      function targetIL(cv) {
        if (!cv?.children) return [cv];
        else return cv.children.reduce(flattenIL, []);
      }
      return [...pv, ...targetIL(cv)];
    }
    return menuItems.reduce(flattenIL, []);
  }, [menuItems]);

  return (
    <Menu
      mode="inline"
      className="main-menu"
      inlineCollapsed={collapsed}
      items={collapsed ? collapsedMenuItems : menuItems}
      defaultSelectedKeys={selectedKey}
      defaultOpenKeys={collapsed ? [] : defaultOpenKeys}
      inlineIndent={7}
    />
  );
};

export default MenuApp;
